(function () {
  if (window.BulkOrderCartPricing) return;

  const BULK_FLAG = '_bulk_order_pricing';
  const TIER_COUNT = 3;
  let queue = Promise.resolve();

  function getRoute(type) {
    const routes = window.MinimogSettings?.routes || {};
    const root = routes.root || window.Shopify?.routes?.root || '/';

    if (type === 'cart') {
      const cartUrl = routes.cart || `${root.replace(/\/$/, '')}/cart`;
      return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
    }
    if (type === 'add') return routes.cart_add_url || `${root.replace(/\/$/, '')}/cart/add.js`;
    if (type === 'change') return routes.cart_change_url || `${root.replace(/\/$/, '')}/cart/change.js`;
    return root;
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      }
    });
    const responseText = await response.text();
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (error) {
      throw new Error('The cart returned an invalid response. Please refresh the page and try again.');
    }
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Unable to update bulk pricing.');
    }
    return result;
  }

  function isBulkItem(item) {
    return item?.properties?.[BULK_FLAG] === '1';
  }

  function getTiers(item) {
    return Array.from({ length: TIER_COUNT }, (_, index) => {
      const tier = index + 1;
      return {
        minimum: Number(item.properties[`_bulk_tier_${tier}_minimum`] || 0),
        variantId: String(item.properties[`_bulk_tier_${tier}_variant`] || '')
      };
    })
      .filter((tier) => tier.minimum > 0 && tier.variantId && tier.variantId !== '0')
      .sort((a, b) => a.minimum - b.minimum);
  }

  function desiredVariantId(item, totalRolls) {
    let activeTier = null;
    getTiers(item).forEach((tier) => {
      if (totalRolls >= tier.minimum) activeTier = tier;
    });
    return activeTier?.variantId || '';
  }

  async function changeLine(key, quantity) {
    return request(getRoute('change'), {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async function addReplacement(item, variantId, quantity) {
    return request(getRoute('add'), {
      method: 'POST',
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity,
          properties: item.properties
        }]
      })
    });
  }

  async function synchronizeNow(options = {}) {
    const cart = await request(getRoute('cart'), { cache: 'no-store' });
    const requestedLine = Number(options.line || 0);
    const requestedQuantity = Math.max(0, Math.floor(Number(options.quantity || 0)));
    const requestedItem = requestedLine > 0 ? cart.items[requestedLine - 1] : null;

    if (requestedLine > 0 && !isBulkItem(requestedItem)) {
      return { handled: false, changed: false, cart };
    }

    const bulkItems = cart.items.filter(isBulkItem).map((item) => ({
      item,
      quantity: item === requestedItem ? requestedQuantity : item.quantity
    }));

    if (!bulkItems.length) {
      return { handled: requestedLine > 0, changed: false, cart };
    }

    const totalRolls = bulkItems.reduce((sum, entry) => {
      const rollsPerCase = Math.max(1, Number(entry.item.properties._bulk_rolls_per_case || 1));
      return sum + (entry.quantity * rollsPerCase);
    }, 0);

    const plan = bulkItems.map((entry) => ({
      ...entry,
      desiredVariantId: entry.quantity > 0 ? desiredVariantId(entry.item, totalRolls) : ''
    }));

    const invalid = plan.find((entry) => entry.quantity > 0 && !entry.desiredVariantId);
    if (invalid) throw new Error('No valid bulk price tier is available for this quantity.');

    let changed = false;

    // First apply quantity-only changes to lines that stay in the same tier.
    for (const entry of plan) {
      const sameVariant = String(entry.item.variant_id) === entry.desiredVariantId;
      if (sameVariant && entry.quantity !== entry.item.quantity) {
        await changeLine(entry.item.key, entry.quantity);
        changed = true;
      } else if (entry.quantity === 0 && entry.item.quantity > 0) {
        await changeLine(entry.item.key, 0);
        changed = true;
      }
    }

    // A tier change requires adding the correctly priced variant, then removing
    // the old one. Adding first prevents a failed add from deleting the item.
    for (const entry of plan) {
      if (entry.quantity < 1 || String(entry.item.variant_id) === entry.desiredVariantId) continue;
      await addReplacement(entry.item, entry.desiredVariantId, entry.quantity);
      await changeLine(entry.item.key, 0);
      changed = true;
    }

    const updatedCart = changed ? await request(getRoute('cart'), { cache: 'no-store' }) : cart;
    return { handled: requestedLine > 0, changed, cart: updatedCart, totalRolls };
  }

  function enqueue(options) {
    const task = queue.then(() => synchronizeNow(options));
    queue = task.catch(() => {});
    return task;
  }

  window.BulkOrderCartPricing = {
    isBulkItem,
    sync() {
      return enqueue({});
    },
    updateLine(line, quantity) {
      return enqueue({ line, quantity });
    }
  };
})();
